import { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { notification } from 'antd';
import type { CredentialResponse } from '@react-oauth/google';
import { useTranslation } from 'next-i18next';
import { useAppDispatch } from '~/shared/hooks/redux';
import { login, checkAuthorization } from '~/rtk-store/auth';
import { setSavedPrfileData } from '~/rtk-store/ecomerce';
import { API_URLS } from '~/shared/config';
import { useLoginMutation, useGetProfileQuery } from '../api';
import { skipToken } from '@reduxjs/toolkit/query';
import { LoginResponse } from '../types';

export type SegmentType = 'email' | 'phone';

export interface LoginFormValues {
    email?: string;
    phone?: string;
    password: string;
}

export const useLoginLogic = () => {
    const router = useRouter();
    const dispatch = useAppDispatch();
    const { t } = useTranslation('common');
    const passwordInputRef = useRef<any>(null); // Type 'any' retained temporarily for Antd Input ref compatibility if strict type fails, but ideally should be InputRef

    // Form state
    const [segmentValue, setSegmentValue] = useState<SegmentType>('email');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);
    const [loginRole, setLoginRole] = useState<string | null>(null);

    // RTK Query hooks
    const [loginUser] = useLoginMutation();

    // Fetch profile after successful login
    const { data: profileData } = useGetProfileQuery(
        accessToken ? undefined : skipToken,
        { skip: !accessToken }
    );

    // Query params
    const { deal, id, return_url } = router.query as { 
        deal?: string; 
        id?: string; 
        return_url?: string;
    };

    // Effects
    useEffect(() => {
        dispatch(checkAuthorization());
    }, [dispatch]);

    useEffect(() => {
        if (profileData && accessToken) {
            dispatch(setSavedPrfileData(profileData));
        }
    }, [profileData, accessToken, dispatch]);

    useEffect(() => {
        if (!pendingRedirect || !loginRole) return;

        if (loginRole !== 'seller') {
            router.push(pendingRedirect);
            setPendingRedirect(null);
            return;
        }

        if (!profileData) return;

        const isBlockedAndForever = Boolean(profileData.is_blocked && profileData.is_forever);
        const targetUrl = isBlockedAndForever ? '/seller/blocked' : pendingRedirect;
        router.push(targetUrl);
        setPendingRedirect(null);
    }, [pendingRedirect, loginRole, profileData, router]);

    // Handlers
    const getRedirectUrl = useCallback((role?: string): string => {
        // First priority: return_url (from middleware redirect)
        if (return_url) {
            // Decode return_url if needed and validate it's a valid path
            try {
                const decodedUrl = decodeURIComponent(return_url);
                // Basic validation - ensure it's a path (starts with /)
                if (decodedUrl.startsWith('/')) {
                    return decodedUrl;
                }
            } catch (e) {
                // If decoding fails, fall through to default redirect
                console.error('Failed to decode return_url:', e);
            }
        }

        // Second priority: id (checkout)
        if (id) return `/account/checkout-one?id=${id}`;
        
        // Third priority: deal
        if (deal) {
            // If deal exists, redirect based on role
            if (role === 'seller') return '/seller/dashboard';
            if (role === 'admin') return '/admin/dashboard';
            return '/seller/dashboard'; // Default to seller
        }
        
        // Fourth priority: Role-based dashboard redirect
        if (role === 'seller') return '/seller/dashboard';
        if (role === 'admin') return '/admin/dashboard';
        return '/seller/dashboard'; // Default fallback
    }, [id, deal, return_url]);

    const buildGoogleLoginUrl = useCallback((): string => {
        const queryParams = new URLSearchParams();
        const utmSource = localStorage.getItem('utm_source');
        const sanitizedUtmSource = utmSource
            ? utmSource.replace(/[^a-zA-Z0-9_-]/g, '')
            : null;

        if (id) {
            queryParams.set('id', String(id));
        }

        if (sanitizedUtmSource) {
            queryParams.set('utm_source', sanitizedUtmSource);
        }

        const queryString = queryParams.toString();
        return `${API_URLS.digital}/auth/new-google-login/seller${
            queryString ? `?${queryString}` : ''
        }`;
    }, [id]);

    const handleLoginSubmit = async (values: LoginFormValues): Promise<void> => {
        const phoneOrEmail = values.phone
            ? `+998${values.phone}`
            : values.email || '';

        const payload = {
            phone_or_email: phoneOrEmail,
            password: values.password,
        };

        setIsSubmitting(true);

        try {
            const userData = await loginUser(payload).unwrap() as LoginResponse;

            if (userData.access) {
                setAccessToken(userData.access);
            }

            // Dispatch login action
            // @ts-ignore - The 'user' property in login action might expect a different structure than LoginResponse
            dispatch(login({ user: userData, data: payload }));

            notification.success({
                message: t('auth.login.success.title'),
                description: t('auth.login.success.description'),
            });

            const redirectUrl = getRedirectUrl(userData.role);
            setLoginRole(userData.role || null);
            setPendingRedirect(redirectUrl);
        } catch (error: any) {
            const errorMessage = error?.data?.msg || t('auth.login.error.default');
            notification.error({
                message: errorMessage,
                duration: 5,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLoginSuccess = async (
        credentialResponse: CredentialResponse
    ): Promise<void> => {
        const idToken = credentialResponse.credential;

        if (!idToken) {
            notification.error({
                message: t('auth.login.error.default'),
                duration: 5,
            });
            return;
        }

        setIsGoogleLoading(true);

        try {
            const response = await fetch(buildGoogleLoginUrl(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id_token: idToken }),
            });
            const userData = await response.json();

            if (!response.ok || !userData?.success || !userData?.access) {
                const fallbackMessage =
                    response.status === 429
                        ? 'Google orqali kirish urinishlari ko‘payib ketdi. Keyinroq qayta urinib ko‘ring.'
                        : t('auth.login.error.default');
                throw new Error(userData?.msg || fallbackMessage);
            }

            setAccessToken(userData.access);

            dispatch(
                login({
                    user: userData,
                    data: {
                        phone_or_email: 'google',
                        role: userData.role || 'seller',
                    },
                })
            );

            notification.success({
                message: t('auth.login.success.title'),
                description: t('auth.login.success.description'),
            });

            const redirectUrl = getRedirectUrl(userData.role);
            setLoginRole(userData.role || null);
            setPendingRedirect(redirectUrl);
        } catch (error: any) {
            notification.error({
                message: error?.message || t('auth.login.error.default'),
                duration: 5,
            });
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleGoogleLoginError = (): void => {
        notification.error({
            message: t('auth.login.error.default'),
            duration: 5,
        });
    };

    const handleTelegramLogin = (): void => {
        router.push({
            pathname: '/auth/telegram',
            query: router.query, // This includes return_url if present
        });
    };

    const getLoginLink = (): string => {
        const queryParams = new URLSearchParams();
        
        // Preserve return_url if it exists
        if (return_url) {
            queryParams.set('return_url', return_url);
        }
        if (id) {
            queryParams.set('id', id);
        }
        if (deal) {
            queryParams.set('deal', deal);
        }
        
        const queryString = queryParams.toString();
        return queryString ? `/auth/login?${queryString}` : '/auth/login';
    };

    return {
        segmentValue,
        setSegmentValue,
        isSubmitting,
        isGoogleLoading,
        passwordInputRef,
        handleLoginSubmit,
        handleGoogleLoginSuccess,
        handleGoogleLoginError,
        handleTelegramLogin,
        getLoginLink,
    };
};
